import { c as _c } from "react/compiler-runtime";
import { Identifier } from "@/types/domain";
import { Locale } from "@/types/locale";
import { IconSelector, IconUser } from "@tabler/icons-react";
import React, { useCallback, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { SwitchLocaleToggle } from "@components/common/buttons/switch-locale-toggle";
import { useSidePanel } from "@components/common/containers/side-panel";
import { ContentViewBlock, ContentViewBlockProps } from "@components/common/content/content-view-block";
import { Button } from "@components/ui/button";
import { ButtonWithTooltip } from "@components/ui/custom/button-with-tooltip";
import { localizeFrom, useAnyTranslations } from "@lib/i18n/utils";
import { buildContributorPanelContent } from "@features/contributors/contributor-panel";
import { resolveContributorContactEmailForDisplay } from "@features/proposals/activity-member-contact-display";
import { ActivityMemberEntity, ActivityMemberInputView } from "@features/proposals/types/member";
import { MemberBiographyViewBlock, SHORT_MODE_THRESHOLD } from "@features/proposals/ui/member/member-biography-view-block";
import { MemberConsentsDialog } from "@features/proposals/ui/member/member-consents-dialog";
import { MemberContentEditBlock } from "@features/proposals/ui/member/member-content-edit-block";
import { MemberDeleteBlock } from "@features/proposals/ui/member/member-delete-block";
import { MemberRoleBadge } from "@features/proposals/ui/member/member-role-badge";
interface ActivityMemberContentLocalizableViewBlockProps extends Omit<ContentViewBlockProps, "title" | "description"> {
  member: ActivityMemberEntity;
  patchMember: (member: ActivityMemberEntity, body: Partial<ActivityMemberInputView>) => Promise<undefined | ActivityMemberEntity>;
  postMemberPhoto?: (id: Identifier, body: FormData) => Promise<undefined | ActivityMemberEntity>;
  isSingleMember: boolean;
  useShortMode?: boolean;
}
export const ActivityMemberContentLocalizableViewBlock = t0 => {
  const $ = _c(89);
  let actions;
  let isSingleMember;
  let member;
  let patchMember;
  let postMemberPhoto;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      member,
      actions,
      patchMember,
      postMemberPhoto,
      isSingleMember,
      useShortMode: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actions;
    $[2] = isSingleMember;
    $[3] = member;
    $[4] = patchMember;
    $[5] = postMemberPhoto;
    $[6] = props;
    $[7] = t1;
  } else {
    actions = $[1];
    isSingleMember = $[2];
    member = $[3];
    patchMember = $[4];
    postMemberPhoto = $[5];
    props = $[6];
    t1 = $[7];
  }
  const useShortMode = t1 === undefined ? false : t1;
  const tAnyContributorFeature = useAnyTranslations("features.contributors");
  const tCommon = useTranslations("common");
  const t = useTranslations("features.proposals.ui.activityMemberContentLocalizableViewBlock");
  const {
    openPanel,
    updatePanel
  } = useSidePanel();
  const systemLocale = useLocale();
  let t2;
  if ($[8] !== member.eventProfile?.biography?.content?.data?.firstName) {
    t2 = Object.entries(member?.eventProfile?.biography?.content?.data?.firstName ?? {}).map(_temp);
    $[8] = member.eventProfile?.biography?.content?.data?.firstName;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const locales = t2;
  const [locale_0, setLocale] = useState(systemLocale);
  const biography = member?.eventProfile?.biography;
  const bioData = biography?.content?.data;
  const t3 = bioData?.firstName;
  let t4;
  if ($[10] !== locale_0 || $[11] !== t3) {
    t4 = localizeFrom(t3, locale_0);
    $[10] = locale_0;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const firstName = t4;
  const t5 = bioData?.lastName;
  let t6;
  if ($[13] !== locale_0 || $[14] !== t5) {
    t6 = localizeFrom(t5, locale_0);
    $[13] = locale_0;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const lastName = t6;
  let t7;
  if ($[16] !== firstName || $[17] !== lastName) {
    t7 = firstName || lastName ? `${firstName} ${lastName}`.trim() : "-";
    $[16] = firstName;
    $[17] = lastName;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const fullName = t7;
  const t8 = bioData?.description;
  let t9;
  if ($[19] !== locale_0 || $[20] !== t8) {
    t9 = localizeFrom(t8, locale_0);
    $[19] = locale_0;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const description = t9;
  let t10;
  if ($[22] !== member.metadata) {
    t10 = resolveContributorContactEmailForDisplay(undefined, member.metadata);
    $[22] = member.metadata;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const contactEmail = t10;
  const [shortMode, setShortMode] = useState(description?.length > SHORT_MODE_THRESHOLD ? useShortMode : false);
  let t11;
  if ($[24] !== member.metadata || $[25] !== openPanel || $[26] !== tAnyContributorFeature || $[27] !== updatePanel) {
    t11 = (contributorId, fullName_0) => {
      const panelId = `contributor-${contributorId}`;
      const payload = {
        title: fullName_0,
        description: tAnyContributorFeature("pages.contributor.description"),
        size: "xl" as const,
        bookmarkLabel: tAnyContributorFeature("domain.contributor.label"),
        children: buildContributorPanelContent(contributorId, {
          activityMemberMetadata: member.metadata
        })
      };
      openPanel(panelId, payload);
      updatePanel(panelId, payload);
    };
    $[24] = member.metadata;
    $[25] = openPanel;
    $[26] = tAnyContributorFeature;
    $[27] = updatePanel;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleOpenContributor = t11;
  let t12;
  if ($[29] !== member || $[30] !== patchMember) {
    t12 = body => patchMember(member, body);
    $[29] = member;
    $[30] = patchMember;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const patchMemberHandler = t12;
  let t13;
  if ($[32] !== member.id || $[33] !== postMemberPhoto) {
    t13 = body_0 => postMemberPhoto?.(member.id, body_0) as Promise<void>;
    $[32] = member.id;
    $[33] = postMemberPhoto;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const postMemberPhotoHandler = t13;
  let t14;
  if ($[35] !== t) {
    t14 = t("title");
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = member?.isMe ? "true" : "false";
  let t16;
  if ($[37] !== t || $[38] !== t15) {
    t16 = t("description", {
      isMe: t15
    });
    $[37] = t;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== locale_0 || $[41] !== locales) {
    t17 = <SwitchLocaleToggle locale={locale_0} setLocale={setLocale as never} locales={locales} size="sm" buttonClassName="last:rounded-r-none" />;
    $[40] = locale_0;
    $[41] = locales;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== fullName || $[44] !== handleOpenContributor || $[45] !== member.contributorId || $[46] !== tAnyContributorFeature) {
    t18 = member.contributorId && <ButtonWithTooltip size="icon-sm" variant="outline" onClick={() => handleOpenContributor(member.contributorId as Identifier, fullName)} tooltipContent={tAnyContributorFeature("pages.contributor.description")}><IconUser /></ButtonWithTooltip>;
    $[43] = fullName;
    $[44] = handleOpenContributor;
    $[45] = member.contributorId;
    $[46] = tAnyContributorFeature;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== isSingleMember || $[49] !== member) {
    t19 = <MemberDeleteBlock member={member} size="icon-sm" variant="outline" isSingleMember={isSingleMember} />;
    $[48] = isSingleMember;
    $[49] = member;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== member || $[52] !== patchMemberHandler || $[53] !== postMemberPhotoHandler) {
    t20 = <MemberContentEditBlock initData={member} patch={patchMemberHandler} uploadPhoto={postMemberPhotoHandler} />;
    $[51] = member;
    $[52] = patchMemberHandler;
    $[53] = postMemberPhotoHandler;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  const t21 = actions && actions;
  let t22;
  if ($[55] !== t17 || $[56] !== t18 || $[57] !== t19 || $[58] !== t20 || $[59] !== t21) {
    t22 = <>{t17}{t18}{t19}{t20}{t21}</>;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== description || $[62] !== shortMode || $[63] !== tCommon || $[64] !== useShortMode) {
    t23 = description?.length > SHORT_MODE_THRESHOLD && useShortMode && description && <Button variant="link" className="mt-auto text-xs" onClick={() => setShortMode(_temp2)}>{shortMode ? tCommon("words.verbs.show-more") : tCommon("words.verbs.hide")}<IconSelector className="h-4 w-4" /></Button>;
    $[61] = description;
    $[62] = shortMode;
    $[63] = tCommon;
    $[64] = useShortMode;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== member.role) {
    t24 = <MemberRoleBadge role={member.role} />;
    $[66] = member.role;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== member || $[69] !== patchMember) {
    t25 = <MemberConsentsDialog member={member} patchMember={patchMember} />;
    $[68] = member;
    $[69] = patchMember;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t24 || $[72] !== t25) {
    t26 = <div className="flex w-full items-center justify-end gap-2 border-t pt-2">{t24}{t25}</div>;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== t23 || $[75] !== t26) {
    t27 = <>{t23}{t26}</>;
    $[74] = t23;
    $[75] = t26;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  const t28 = member?.eventProfile?.biography;
  let t29;
  if ($[77] !== contactEmail || $[78] !== locale_0 || $[79] !== shortMode || $[80] !== t28) {
    t29 = <MemberBiographyViewBlock biography={t28} shortMode={shortMode} locale={locale_0} contactEmail={contactEmail} />;
    $[77] = contactEmail;
    $[78] = locale_0;
    $[79] = shortMode;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== props || $[83] !== t14 || $[84] !== t16 || $[85] !== t22 || $[86] !== t27 || $[87] !== t29) {
    t30 = <ContentViewBlock title={t14} description={t16} {...props} actions={t22} footer={t27}>{t29}</ContentViewBlock>;
    $[82] = props;
    $[83] = t14;
    $[84] = t16;
    $[85] = t22;
    $[86] = t27;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  return t30;
};
function _temp(t0) {
  const [locale] = t0;
  return locale as Locale;
}
function _temp2(s) {
  return !s;
}